import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Spinner } from "@yleisradio/yds-components-react";
import { color } from "@yleisradio/yds-core";
import Hls from "hls.js";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import { shallow } from "zustand/shallow";

import { useAdobeMediaSDK } from "../analytics/useAdobeMediaSDK";
import { useFinnpanelEvent } from "../analytics/useFinnpanelEvent";
import { useSnowplow } from "../analytics/useSnowplow";
import * as Constants from "../constants";
import { LIVE_EDGE_THRESHOLD_IN_SECONDS } from "../constants";
import { useEmbedParametersContext } from "../contexts/embedParametersContext";
import { useMediaContext } from "../contexts/MediaContext";
import { PlayerContext } from "../contexts/playerContext";
import * as Enums from "../enums";
import { usePrevious } from "../helpers/commonUtils";
import { PauseableTimeout } from "../helpers/pauseableTimeout";
import { videoPlaybackRates } from "../helpers/playbackRates";
import { resolveServiceScheduleFn } from "../helpers/serviceSchedule";
import useBindVideoEventListeners from "../hooks/useBindVideoEventListeners/useBindVideoEventListeners";
import useHls from "../hooks/useHls/useHls";
import { useMux } from "../hooks/useMux/useMux";
import { useStoreSubscribe } from "../hooks/useStoreSubscribe/useStoreSubscribe";
import useTextTracks from "../hooks/useTextTracks/useTextTracks";
import { usePlayerStore } from "../store/usePlayerStore";
import {
  PlayerEvent,
  PlayerEventCallback,
  PlayerRefreshEvent,
  PlayerUpdateEvent,
  SetPlayerApiFn
} from "../types/common";

import { PreviewPlayButton } from "./PlayButton/PreviewPlayButton";
import { enterFullscreen, exitFullscreen } from "./player-ui/Fullscreen.util";
import { resolveSelectedAudioTrack } from "./player-ui/helpers";
import { togglePictureInPicture } from "./player-ui/PictureInPicture";
import { PlayerUI } from "./player-ui/PlayerUI";
import { PlayerUIBackgroundVideo } from "./player-ui/PlayerUIBackgroundVideo";
import { resolveAspectRatio } from "./AspectRatio";
import VideoElement from "./VideoElement";

const VideoContainer = styled.div<{
  playbackStarted: boolean;
}>`
  height: 100%;
  position: absolute;
  top: 0;
  width: 100%;

  ${props => {
    if (props.playbackStarted) {
      return `
        background-color: ${color.BLACK};
      `;
    }
  }}
`;

const SpinnerWrap = styled.div`
  @keyframes spinner-fade-in {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
  animation: spinner-fade-in 1s forwards;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
`;

export const YlePlayer = ({
  refreshComponent,
  setPlayerApi,
  update
}: {
  refreshComponent: PlayerRefreshEvent;
  setPlayerApi: SetPlayerApiFn;
  update: PlayerUpdateEvent;
}) => {
  const { getState, subscribe, useStoreState } = usePlayerStore();
  const analyticsEvents = useStoreState.analyticsEvents();
  const snowplowEvents = useStoreState.snowplowEvents();
  const audioTracks = useStoreState.audioTracks();
  const device = useStoreState.device();
  const programId = useStoreState.programId();
  const errorOverlayVisible = useStoreState.errorOverlayVisible();
  const externalEvents = useStoreState.externalEvents();
  const hlsJs = useStoreState.hlsJs();
  const isCasting = useStoreState.isCasting();
  const isFakeOnDemand = useStoreState.isFakeOnDemand();
  const isLive = useStoreState.isLive();
  const isLoading = useStoreState.isLoading();
  const isPaused = useStoreState.isPaused();
  const isPictureInPicture = useStoreState.isPictureInPicture();
  const isPlaybackStarted = useStoreState.isPlaybackStarted();
  const isSimulcast = useStoreState.isSimulcast();
  const player = useStoreState.player();
  const playerElement = useStoreState.playerElement();
  const playerUiType = useStoreState.playerUiType();
  const userPreferredAudioTrack = useStoreState.userPreferredAudioTrack();

  const setAspectRatio = useStoreState.setAspectRatio();
  const setAudioTracks = useStoreState.setAudioTracks();
  const setIsChatButtonEnabled = useStoreState.setIsChatButtonEnabled();
  const setIsDestroyed = useStoreState.setIsDestroyed();
  const setIsEnded = useStoreState.setIsEnded();
  const setIsLiveEdge = useStoreState.setIsLiveEdge();
  const setIsLoading = useStoreState.setIsLoading();
  const setPlayerUiType = useStoreState.setPlayerUiType();
  const setScheduledProgram = useStoreState.setScheduledProgram();
  const setSelectedAudioTrackId = useStoreState.setSelectedAudioTrackId();
  const setSubtitles = useStoreState.setSubtitles();
  const setVsId = useStoreState.setVsId();

  const { autoplay, controls, language, playButton, playFullScreen, spinner } =
    useEmbedParametersContext();
  const { dvrWindowInSeconds, serviceSchedule, streamAspectRatio } =
    useMediaContext();
  const { onSeekStart } = useStoreSubscribe();

  // States
  const [destroy, setDestroy] = useState<boolean>(false);
  const [isMediaAttached, setIsMediaAttached] = useState<boolean>(false);
  const [showLoading, setShowLoading] = useState(false);
  const [uuId, setUuId] = useState<string>(uuidv4());
  const [video, setVideo] = useState<HTMLVideoElement>();

  // Refs
  const loadingTimeoutRef = useRef<number>();
  const serviceScheduleTimer = useRef<PauseableTimeout>();
  const uiRef = useRef<HTMLDivElement>(null);
  const previousPlayerUiType = useRef<Enums.PlayerUiType>(
    getState().playerUiType
  );
  const playerPromiseResolvedRef = useRef(false);

  // Others
  const previousProgramId = usePrevious(programId);
  const previousIsCasting = usePrevious(isCasting);

  useSnowplow();
  useAdobeMediaSDK({
    playerName: "YlePlayer __PLAYER_STATIC_BUILD_VERSION__"
  });
  useFinnpanelEvent();

  const usingServiceScheduleTracks = !!(
    serviceSchedule && serviceSchedule.length > 0
  );

  useEffect(() => {
    if (!!previousProgramId && programId !== previousProgramId) {
      setUuId(uuidv4());
    }
  }, [programId, previousProgramId]);

  useEffect(() => {
    setVsId(uuId);
  }, [setVsId, uuId]);

  useEffect(() => {
    if (destroy) {
      if (isPictureInPicture) togglePictureInPicture(video);
      setIsDestroyed(true);
      setDestroy(false);
      analyticsEvents?.onFinish();
      snowplowEvents?.onDestroy();
    }
  }, [
    analyticsEvents,
    snowplowEvents,
    destroy,
    setIsDestroyed,
    isPictureInPicture,
    video
  ]);

  useBindVideoEventListeners(video);

  const hls = useHls({
    destroy,
    setIsMediaAttached,
    usingServiceScheduleTracks,
    video
  });

  useMux({
    hls,
    mediaElement: video,
    type: Hls.isSupported() ? "hlsVideo" : "nativeVideo"
  });

  useTextTracks({ video });

  const resolveSchedule = useCallback(
    (targetTime: number | null) => {
      const state = getState();
      resolveServiceScheduleFn({
        isFakeOnDemand,
        isSimulcast,
        paused: state.isPaused,
        programId: state.programId,
        refresh: refreshComponent,
        serviceSchedule,
        serviceScheduleTimer,
        setAudioTracks,
        setScheduledProgram,
        setSubtitles,
        targetTime
      });
    },
    [
      getState,
      isFakeOnDemand,
      isSimulcast,
      serviceSchedule,
      setAudioTracks,
      setScheduledProgram,
      setSubtitles,
      refreshComponent
    ]
  );

  const resolveScheduleForCurrentTimelinePosition = useCallback(() => {
    const seekableEnd = getState().seekableEnd;
    if (isSimulcast && video && seekableEnd) {
      const timelineBeginsAt = Date.now() - seekableEnd * 1000;
      resolveSchedule(timelineBeginsAt + video.currentTime * 1000);
    }
  }, [getState, isSimulcast, resolveSchedule, video]);

  useEffect(() => {
    if (isLoading && !showLoading) {
      loadingTimeoutRef.current = setTimeout(() => {
        setShowLoading(true);
      }, 1000) as unknown as number;
    } else if (!isLoading && showLoading) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = undefined;
      setShowLoading(false);
    }
  }, [isLoading, showLoading]);

  useEffect(() => {
    if (isPlaybackStarted && video) {
      const seekableEnd = getState().seekableEnd;
      const _currentTime = Math.round(video.currentTime);
      const millisecondsInPast =
        _currentTime && _currentTime > 0 && seekableEnd && seekableEnd > 0
          ? (seekableEnd - _currentTime) * 1000
          : 0;

      resolveSchedule(isSimulcast ? Date.now() - millisecondsInPast : null);
    }
  }, [
    getState,
    isSimulcast,
    isPlaybackStarted,
    resolveSchedule,
    serviceSchedule,
    video
  ]);

  useEffect(() => {
    if (!isCasting && previousIsCasting) {
      resolveScheduleForCurrentTimelinePosition();
    }
  }, [isCasting, previousIsCasting, resolveScheduleForCurrentTimelinePosition]);

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      serviceScheduleTimer.current?.clear();
      clearTimeout(loadingTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (isPlaybackStarted && playFullScreen) {
      enterFullscreen(device.isMobile, playerElement, video);
    }
  }, [device, isPlaybackStarted, playFullScreen, playerElement, video]);

  useEffect(() => {
    // Non seekable live doesn't have timeline.
    // After pause, set the current time to live edge.
    const isNonSeekableLive = isLive && !dvrWindowInSeconds;
    const _seekableEnd = getState().seekableEnd;
    if (!isPaused && isNonSeekableLive && _seekableEnd) {
      player.seekTo(_seekableEnd);
    }
  }, [dvrWindowInSeconds, getState, isLive, isPaused, player]);

  useEffect(() => {
    if (isLive) {
      const unsubscribe = subscribe(
        state => [state.seekableEnd, state.currentTime],
        ([_seekableEnd, currentTime]) => {
          if (_seekableEnd && currentTime) {
            setIsLiveEdge(
              _seekableEnd - currentTime <= LIVE_EDGE_THRESHOLD_IN_SECONDS
            );
          }
        },
        {
          equalityFn: shallow
        }
      );
      return () => {
        unsubscribe();
      };
    }
  }, [getState, isLive, setIsLiveEdge, subscribe]);

  useEffect(() => {
    if (isPaused) {
      serviceScheduleTimer.current?.pause();
    }
  }, [isPaused]);

  useEffect(() => {
    const unsub = onSeekStart(() => {
      setIsEnded(false);
      resolveScheduleForCurrentTimelinePosition();
    });
    return () => {
      unsub();
    };
  }, [onSeekStart, resolveScheduleForCurrentTimelinePosition, setIsEnded]);

  useEffect(() => {
    if (errorOverlayVisible) {
      setIsLoading(false);
    }
  }, [errorOverlayVisible, setIsLoading]);

  const togglePlayerUiType = useCallback(
    (showUi: boolean) => {
      if (controls) {
        if (showUi) {
          setPlayerUiType(previousPlayerUiType.current);
        } else {
          const currentUiType = getState().playerUiType;
          if (currentUiType !== Enums.PlayerUiType.hiddenControls) {
            previousPlayerUiType.current = getState().playerUiType;
          }
          setPlayerUiType(Enums.PlayerUiType.hiddenControls);
        }
      } else {
        console.warn("Controls are disabled");
      }
    },
    [controls, getState, setPlayerUiType]
  );

  useEffect(() => {
    if (
      video &&
      isMediaAttached &&
      externalEvents &&
      !playerPromiseResolvedRef.current
    ) {
      setPlayerApi({
        closeFullscreen: () => {
          exitFullscreen();
        },
        defaultParameters: () => Constants.PLAYER_DEFAULT_VALUES,
        destroy: () => setDestroy(true),
        focus: () => {
          uiRef.current?.dispatchEvent(new CustomEvent("quietFocus"));
        },
        hideChatButton: () => setIsChatButtonEnabled(false),
        hideControls: () => togglePlayerUiType(false),
        mute: () => {
          getState().player.setMuted(true, true);
          return true;
        },
        muted: () => getState().isMuted,
        on: (eventName: PlayerEvent, callback: PlayerEventCallback) => {
          if (Constants.playerEventOptions.includes(eventName)) {
            externalEvents.addListener(eventName, callback);
          }
        },
        openFullscreen: () => {
          enterFullscreen(getState().device.isMobile, playerElement, video);
        },
        pause: () => getState().player.pause(),
        play: () => getState().player.play({ throwError: true }),
        playbackRates: () => videoPlaybackRates,
        seek: seekTime => getState().player.seekTo(seekTime),
        setAspectRatio: aspectRatio => {
          setAspectRatio(
            resolveAspectRatio(aspectRatio, getState().media?.streamAspectRatio)
          );
        },
        setPlaybackRate: getState().player.setPlaybackRate,
        showChatButton: () => setIsChatButtonEnabled(true),
        showControls: () => togglePlayerUiType(true),
        startLoad: getState().hlsJs.startLoad,
        stopLoad: getState().hlsJs.stopLoad,
        unmute: () => {
          getState().player.setMuted(false, true);
          return false;
        },
        volume: value => {
          getState().player.setVolume(value);
          return value;
        }
      });
      playerPromiseResolvedRef.current = true;
    }
  }, [
    externalEvents,
    getState,
    hlsJs,
    isMediaAttached,
    player,
    playerElement,
    setAspectRatio,
    setIsChatButtonEnabled,
    setPlayerApi,
    streamAspectRatio,
    togglePlayerUiType,
    update,
    video
  ]);

  useEffect(() => {
    resolveSelectedAudioTrack(
      audioTracks,
      language,
      setSelectedAudioTrackId,
      userPreferredAudioTrack
    );
  }, [audioTracks, language, setSelectedAudioTrackId, userPreferredAudioTrack]);

  const renderPreviewButton =
    playerUiType !== Enums.PlayerUiType.backgroundVideo &&
    !isPlaybackStarted &&
    (playButton || autoplay) &&
    !isCasting &&
    isMediaAttached;

  const userHasClickedPreviewButton = useRef(false);
  const afterPreviewPlayButtonClick = useCallback(() => {
    userHasClickedPreviewButton.current = true;
  }, []);

  const renderPlayerDefaultUi = useMemo(
    () =>
      isPlaybackStarted &&
      (playerUiType === Enums.PlayerUiType.default ||
        playerUiType === Enums.PlayerUiType.hiddenControls),
    [isPlaybackStarted, playerUiType]
  );

  useEffect(() => {
    if (userHasClickedPreviewButton.current && renderPlayerDefaultUi) {
      // Keep the focus on player after the preview play button has been destroyed.
      // Otherwise, it might jump out of the player which can be confusing for screen reader users
      uiRef.current?.focus();
      userHasClickedPreviewButton.current = false;
    }
  }, [renderPlayerDefaultUi]);

  return (
    <VideoContainer playbackStarted={isPlaybackStarted}>
      {spinner && isLoading && !isPaused && !isCasting && (
        <SpinnerWrap>
          <Spinner size="lg" variant="default-negative" />
        </SpinnerWrap>
      )}
      <VideoElement setVideo={setVideo} videoId={uuId} />
      {
        <PlayerContext.Provider value={{ update }}>
          {playerUiType === Enums.PlayerUiType.backgroundVideo && (
            <PlayerUIBackgroundVideo />
          )}
          {renderPreviewButton && (
            <PreviewPlayButton afterPlayClick={afterPreviewPlayButtonClick} />
          )}
          {renderPlayerDefaultUi && <PlayerUI uiRef={uiRef} />}
        </PlayerContext.Provider>
      }
    </VideoContainer>
  );
};
